import React, { useEffect, useState } from 'react';
import { X, History, RotateCcw, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { websiteAPI } from '../../../services/api';

/**
 * HistoryPanel — modal showing the last 10 published versions of the
 * current page. Selecting a version restores it INTO THE DRAFT (not live);
 * the admin must press "Publish" afterwards. This double-click design
 * prevents accidental rollbacks.
 *
 * Props:
 *   open      — boolean
 *   pageKey   — current page key
 *   onClose   — () => void
 *   onRestored — (newState) => void  // notifies parent so it can re-seed
 */
export default function HistoryPanel({ open, pageKey, onClose, onRestored }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restoringIndex, setRestoringIndex] = useState(null);
  const [confirmIndex, setConfirmIndex] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setHistory(null);
    (async () => {
      try {
        const resp = await websiteAPI.pageHistory(pageKey);
        if (cancelled) return;
        const body = resp?.data?.data || resp?.data;
        setHistory(body?.history || []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'تعذّر تحميل السجل');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, pageKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const doRestore = async (index) => {
    setRestoringIndex(index);
    try {
      const resp = await websiteAPI.restorePageHistory(pageKey, index);
      const body = resp?.data?.data || resp?.data;
      toast.success('تمت استعادة النسخة كمسوّدة. اضغط "نشر" لجعلها مباشرة.');
      onRestored?.(body);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّرت الاستعادة');
    } finally {
      setRestoringIndex(null);
      setConfirmIndex(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" dir="rtl">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <History className="w-4 h-4 text-blue-600" />
          <div className="flex-1">
            <h2 className="text-base font-bold text-slate-900">تاريخ النشر</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">آخر {history?.length || 0} نسخة منشورة (الحد الأقصى 10)</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          )}
          {error && !loading && (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600 font-semibold">{error}</p>
            </div>
          )}
          {!loading && !error && history?.length === 0 && (
            <div className="text-center py-12">
              <History className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">لا يوجد نشر سابق لهذه الصفحة</p>
              <p className="text-[11px] text-slate-400 mt-1">سيظهر التاريخ هنا بعد أوّل نشر</p>
            </div>
          )}
          {!loading && !error && history?.length > 0 && (
            <ul className="space-y-2">
              {history.map((h) => {
                const date = h.publishedAt ? new Date(h.publishedAt) : null;
                const dateStr = date ? date.toLocaleString('ar-SA-u-ca-gregory', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
                return (
                  <li key={h.index} className="bg-white border border-slate-200 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900">
                          النسخة #{h.index + 1}
                          {h.label && <span className="mr-2 font-normal text-slate-500">— {h.label}</span>}
                        </p>
                        <p className="text-[12px] text-slate-500 mt-0.5">{dateStr}</p>
                      </div>
                      {confirmIndex === h.index ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => doRestore(h.index)}
                            disabled={restoringIndex !== null}
                            className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-[11px] font-bold hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1"
                          >
                            {restoringIndex === h.index ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                            تأكيد
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmIndex(null)}
                            disabled={restoringIndex !== null}
                            className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-[11px] font-semibold hover:bg-slate-50"
                          >إلغاء</button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmIndex(h.index)}
                          className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg text-[11px] font-bold hover:bg-blue-100 inline-flex items-center gap-1"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          استعادة
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60">
          <p className="text-[11px] text-slate-500 text-center leading-relaxed">
            الاستعادة تُحمِّل النسخة في المسوّدة فقط. لجعلها مباشرة على الموقع، اضغط "نشر" بعد الاستعادة.
          </p>
        </div>
      </div>
    </div>
  );
}
